/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Tue Jun 13 2023
 **/

import { useRef } from "react";
import throttle from "lodash-es/throttle";
import { has } from "lodash-es";

type NotificationEvents = {
	onShow?: () => void;
	onClose?: () => void;
	onClick?: (event: Event, notification: Notification) => void;
	onError?: (error: any) => void;
};

const LOGTAG = "#usePushNotification#";

export default function usePushNotification(events?: NotificationEvents) {
	const notificationRef = useRef<Notification | null>(null);

	if (!window.Notification) {
		console.warn(LOGTAG, "This browser does not support desktop notification");
		return null;
	}

	const createNewNotification = (
		title: string,
		options?: NotificationOptions,
		refClear = false,
		onClick?: (event: Event, notification: Notification) => void
	) => {
		const notification = new Notification(title, options);
		notification.onshow = () => {
			navigator.setAppBadge?.();
			events?.onShow?.();
		};
		notification.onclose = () => {
			navigator.clearAppBadge();
			console.debug(LOGTAG, "notification close");
			events?.onClose?.();
			if (refClear) notificationRef.current = null;
		};
		notification.onclick = (ev: Event) => {
			ev.preventDefault();
			window.focus();
			events?.onClick?.(ev, ev.target as Notification);
			onClick?.(ev, notification);

			if (refClear) {
				notificationRef.current?.close();
				notificationRef.current = null;
			} else {
				notification?.close();
			}
		};
		notification.onerror = (error: any) => events?.onError?.(error);
		return notification;
	};

	return {
		checkPermission() {
			console.debug(LOGTAG, "window.Notification.permission", window.Notification.permission);
			if (window.Notification.permission !== "granted") {
				try {
					window.Notification.requestPermission().then((permission) => {
						if (permission !== "granted") {
							//							alert("알림이 허용되지 않았습니다.");
							return null;
						}
					});
				} catch (error: any) {
					if (error instanceof TypeError) {
						window.Notification.requestPermission((permission) => {
							if (permission !== "granted") {
								return null;
							}
						});
					}
				}
			}
		},
		fireNotification({
			title,
			options,
			...rest
		}: { title: string } & Partial<{
			timeout: number;
			options: NotificationOptions;
			onClick: (event: Event) => void;
		}>) {
			if (window.Notification.permission !== "granted") {
				return;
			}
			if (has(rest, "timeout")) {
				if (notificationRef.current) {
					if (rest.timeout) {
						throttle(() => {
							notificationRef.current?.close();
							notificationRef.current = null;
						}, rest.timeout);
					}
				}
				notificationRef.current = createNewNotification(title, options, true, rest.onClick);
			} else {
				createNewNotification(title, options, false, rest.onClick);
			}
		}
	};
}
